import { useMemo } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

function branchLabel(branch, inputType) {
    if (branch.label) return branch.label;
    if (inputType === 'boolean') {
        return branch.value === true || branch.value === 'true' ? 'Yes' : 'No';
    }
    return String(branch.value);
}

function nodeTitle(node) {
    if (!node) return '';
    if (node.title) return node.title;
    if (node.type === 'terminal') return node.result?.condition || node.id;
    if (node.type === 'action') {
        return node.value || node.catalogFieldName || node.subflowTreeId || node.actionKind || node.id;
    }
    return node.prompt || node.id;
}

function actionEyebrow(node) {
    const kind = node.actionKind || 'productGrade';
    if (kind === 'variant') return 'Variant';
    if (kind === 'listingField') return 'Reassign SKU';
    if (kind === 'catalogField') return 'Catalog field';
    if (kind === 'subflow') return 'Wizard';
    return 'Product Grade';
}

function branchCollapseKey(parentId, idx) {
    return `branch:${parentId}:${idx}`;
}

function FlowNode({
    nodeId,
    nodes,
    selectedId,
    onSelect,
    collapsed,
    setCollapsed,
    depth = 0,
    isStart = false,
    visited = null,
}) {
    const node = nodes[nodeId];
    if (!node) return null;

    // Prevent infinite recursion when next-step links form a cycle
    const seen = visited || new Set();
    if (seen.has(nodeId)) {
        return (
            <div className="insp-meta" style={{ margin: '4px 0 8px 28px' }}>
                (cycle — “{nodeTitle(node)}” already appears above)
            </div>
        );
    }
    const nextVisited = new Set(seen);
    nextVisited.add(nodeId);

    const isQuestion = node.type === 'question';
    const isAction = node.type === 'action' || node.type === 'terminal';
    const isCollapsed = Boolean(collapsed[nodeId]);
    const selected = selectedId === nodeId;
    const branchCount = (node.branches || []).length;
    const actionContinues = isAction && node.nextNodeId && nodes[node.nextNodeId];

    const toggleNode = (e) => {
        e.stopPropagation();
        setCollapsed((prev) => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }));
    };

    const toggleBranch = (key) => (e) => {
        e.stopPropagation();
        setCollapsed((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const showChevron = (isQuestion && branchCount > 0) || actionContinues;

    return (
        <div className={`insp-flow-section ${isStart ? 'is-start' : ''}`}>
            <div className="insp-flow-section-head">
                {showChevron ? (
                    <button
                        type="button"
                        className="insp-flow-chevron"
                        onClick={toggleNode}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
                    >
                        <ExpandMoreRoundedIcon
                            sx={{
                                fontSize: 22,
                                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.15s ease',
                            }}
                        />
                    </button>
                ) : (
                    <span className="insp-flow-chevron-spacer" />
                )}

                <button
                    type="button"
                    className={`insp-node-card ${isAction ? 'result' : ''} ${selected ? 'selected' : ''}`}
                    onClick={() => onSelect(nodeId)}
                >
                    <div className="eyebrow">
                        {isStart ? 'Start · ' : ''}
                        {isAction
                            ? `${actionEyebrow(node)}${node.nextNodeId ? ' · continues' : ''}`
                            : `Question · ${branchCount} answers`}
                    </div>
                    <div className="name">{nodeTitle(node)}</div>
                </button>
            </div>

            {isQuestion && !isCollapsed && (
                <div className="insp-flow-children">
                    {(node.branches || []).map((branch, idx) => {
                        const childId = branch.nextNodeId;
                        const bKey = branchCollapseKey(nodeId, idx);
                        const branchCollapsed = Boolean(collapsed[bKey]);
                        const label = branchLabel(branch, node.inputType);
                        const hasChild = Boolean(childId && nodes[childId]);

                        return (
                            <div key={bKey} className="insp-flow-branch">
                                <button
                                    type="button"
                                    className="insp-flow-branch-toggle"
                                    onClick={toggleBranch(bKey)}
                                    aria-expanded={!branchCollapsed}
                                >
                                    <ExpandMoreRoundedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: '#5C6570',
                                            transform: branchCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.15s ease',
                                        }}
                                    />
                                    <span>If {label}</span>
                                </button>

                                {!branchCollapsed && (
                                    <div className="insp-flow-branch-body">
                                        {hasChild ? (
                                            <FlowNode
                                                nodeId={childId}
                                                nodes={nodes}
                                                selectedId={selectedId}
                                                onSelect={onSelect}
                                                collapsed={collapsed}
                                                setCollapsed={setCollapsed}
                                                depth={depth + 1}
                                                visited={nextVisited}
                                            />
                                        ) : (
                                            <div className="insp-meta" style={{ marginLeft: 4, marginBottom: 8 }}>
                                                (no next step)
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {actionContinues && !isCollapsed && (
                <div className="insp-flow-children">
                    <div className="insp-flow-branch">
                        <div className="insp-flow-branch-toggle" style={{ cursor: 'default' }}>
                            <span>Then</span>
                        </div>
                        <div className="insp-flow-branch-body">
                            <FlowNode
                                nodeId={node.nextNodeId}
                                nodes={nodes}
                                selectedId={selectedId}
                                onSelect={onSelect}
                                collapsed={collapsed}
                                setCollapsed={setCollapsed}
                                depth={depth + 1}
                                visited={nextVisited}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function FlowPane({
    tree,
    selectedId,
    onSelect,
    collapsed,
    setCollapsed,
    onAddResult,
    onAddQuestion,
}) {
    const nodes = tree?.nodes || {};
    const startId = tree?.startNodeId;

    const allCollapseKeys = useMemo(() => {
        const keys = [];
        for (const [id, node] of Object.entries(nodes)) {
            if (node?.type !== 'question') continue;
            keys.push(id);
            (node.branches || []).forEach((_, idx) => {
                keys.push(branchCollapseKey(id, idx));
            });
        }
        return keys;
    }, [nodes]);

    const expandAll = () => setCollapsed({});
    const collapseAll = () => {
        const next = {};
        for (const key of allCollapseKeys) {
            // Keep start question expanded so the tree still has a foothold
            if (key === startId) continue;
            next[key] = true;
        }
        setCollapsed(next);
    };

    return (
        <div className="insp-flow-pane">
            <div className="insp-flow-pane-header">
                <h3>Flow</h3>
                <div className="insp-flow-links">
                    <button type="button" onClick={expandAll}>Expand</button>
                    <button type="button" onClick={collapseAll}>Collapse</button>
                    {onAddQuestion && (
                        <button type="button" onClick={onAddQuestion}>+ Question</button>
                    )}
                    {onAddResult && (
                        <button type="button" onClick={onAddResult}>+ Result</button>
                    )}
                </div>
            </div>
            {startId && nodes[startId] ? (
                <FlowNode
                    nodeId={startId}
                    nodes={nodes}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    isStart
                />
            ) : (
                <p className="insp-meta">No start node configured.</p>
            )}
        </div>
    );
}
